import {
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import { InsertDriveFile, MoreVert } from '@material-ui/icons'
import _ from 'lodash'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { downloadAll, downloadFile } from '../../../actions/fileActions'
import {
  removeFileFromDeliverables,
  submitDeliverables,
  updateProject,
} from '../../../actions/projectActions'
import { useFileInfo } from '../../../hooks/useFileInfo'
import ActionsMenu from '../../ActionsMenu/ActionsMenu'
import MultiFilesButton from '../../Form/MultiFilesButton'
import ToastMessage from '../../ToastMessage/ToastMessage'

const ProjectDeliverables = ({ deliverables, setDeliverables }) => {
  const dispatch = useDispatch()

  const [filesUploading, setFilesUploading] = useState([])
  const [filesError, setFilesError] = useState('')
  const [downloadToast, setDownloadToast] = useState(false)

  const { userInfo } = useSelector((state) => state.userLogin)
  const { project } = useSelector((state) => state.projectDetails)

  // Compare state deliverable to redux deliverables to determine if deliverables are touched
  const deliverablesUntouched = _.isEqual(project.deliverables, deliverables)

  const handleSubmitClick = () => {
    const projectClone = { ...project, deliverables }

    dispatch(submitDeliverables(projectClone))
  }

  const handleAcceptClick = () => {
    let projectClone = { ...project }
    projectClone.status = 'Complete'
    projectClone.event = {
      event: 'Project completed',
      user: userInfo._id,
    }

    dispatch(updateProject(projectClone))
  }

  const handleDownloadAll = () => {
    const downloadList = deliverables.map((x) => x.id)

    setDownloadToast(true)
    dispatch(downloadAll(downloadList))
  }

  const UploadRow = ({ file }) => {
    const { fileSize } = useFileInfo(file)

    return (
      <TableRow>
        <TableCell colSpan={4}>
          <div className="file-upload">
            <div className="m-b-10" style={{ color: '#666666' }}>
              {file.name} {fileSize}
            </div>
            <LinearProgress />
          </div>
        </TableCell>
      </TableRow>
    )
  }

  const DataRow = ({ file }) => {
    const { fileSize, fileName, fileType } = useFileInfo(file)

    const handleRemoveClick = () => {
      const newArray = deliverables.filter((x) => x.id !== file.id)
      const projectClone = { ...project }

      projectClone.deliverables = newArray

      setDeliverables(newArray)
      dispatch(removeFileFromDeliverables(projectClone, fileName, file.id))
    }

    const handleDownloadClick = () => {
      setDownloadToast(true)
      dispatch(downloadFile(file))
    }

    const adminOptions = [
      {
        onClick: () => handleRemoveClick(),
        label: 'Delete',
      },
      {
        onClick: () => handleDownloadClick(),
        label: 'Download',
      },
    ]

    const userOptions = [
      {
        onClick: () => handleDownloadClick(),
        label: 'Download',
      },
    ]

    return (
      <TableRow hover>
        <TableCell className="flex" style={{ maxWidth: '200px' }}>
          <div className="truncate">
            <InsertDriveFile
              style={{
                width: 16,
                height: 16,
                color: '#a7a7a7',
                marginRight: 5,
              }}
            />
            {fileName}
          </div>
        </TableCell>
        <TableCell style={{ maxWidth: '50px' }}>
          <div className="flex" style={{ width: 100 }}>
            {fileSize}
          </div>
        </TableCell>
        <TableCell style={{ maxWidth: '50px' }}>{fileType}</TableCell>
        <TableCell className="actions" align="right">
          <ActionsMenu
            icon={<MoreVert />}
            options={userInfo.isAdmin ? adminOptions : userOptions}
          />
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <div className="blue-bg flex align-center space-between p-l-16 p-r-16 p-t-8 p-b-8">
        <h5 className="fs16 m-b-0">Deliverables</h5>
        {userInfo.isAdmin ? (
          <div className="flex">
            <MultiFilesButton
              filesUploading={filesUploading}
              setFiles={setDeliverables}
              setFilesUploading={setFilesUploading}
              error={filesError}
              setError={setFilesError}
            />

            <Button
              variant="contained"
              className="btn-gradient solid"
              disabled={deliverablesUntouched}
              onClick={handleSubmitClick}
            >
              Submit
            </Button>
          </div>
        ) : (
          <div className="flex">
            {deliverables.length > 0 && (
              <Button
                variant="outlined"
                onClick={handleDownloadAll}
                className="btn-white"
              >
                Download all
              </Button>
            )}

            {project.status !== 'Complete' && (
              <Button
                variant="contained"
                disabled={deliverables.length === 0}
                className="btn-gradient solid m-l-16"
                onClick={handleAcceptClick}
              >
                Accept
              </Button>
            )}
          </div>
        )}
      </div>
      <TableContainer className="scrollable" style={{ maxHeight: 400 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell>File</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Type</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filesUploading.length > 0 &&
              filesUploading
                .slice(0)
                .reverse()
                .map((file, index) => <UploadRow file={file} key={index} />)}

            {deliverables &&
              deliverables.length > 0 &&
              deliverables
                .slice(0)
                .reverse()
                .map((file, index) => <DataRow file={file} key={index} />)}
          </TableBody>
        </Table>
        {deliverables.length === 0 && (
          <div className="empty-container">
            <div className="empty">
              <InsertDriveFile
                style={{ width: 48, height: 48, color: '#a7a7a7' }}
              />
              <p>No files added yet</p>
            </div>
          </div>
        )}
        <div className="table-footer flex flex-end p-l-16 p-r-16 p-t-8 p-b-8 fs14">
          <div>{deliverables.length} files</div>
        </div>
      </TableContainer>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Downloading</p>
            <p className="fs14 m-b-0">
              We are preparing your files for download. Please wait...
            </p>
          </>
        }
        open={downloadToast}
        setOpen={setDownloadToast}
      />
    </>
  )
}

export default ProjectDeliverables
